import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useForm, useFieldArray } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useMedicines } from '../../hooks/useMedicines';
import toast from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiArrowLeft, FiPlus, FiTrash2 } = FiIcons;

const AddMedicineForm = () => {
  const navigate = useNavigate();
  const { addMedicine } = useMedicines();
  const [loading, setLoading] = useState(false);

  const { register, handleSubmit, control, watch, formState: { errors }, setValue } = useForm({
    defaultValues: {
      name: '',
      type: 'tablet',
      dosage: '',
      frequency: 1,
      timing: ['08:00'],
      food_instruction: 'after_food',
      start_date: new Date().toISOString().split('T')[0],
      end_date: '',
      notes: ''
    }
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'timing'
  });

  const frequency = watch('frequency');

  // Update timing fields when frequency changes
  React.useEffect(() => {
    const currentFieldsCount = fields.length;
    const targetFrequency = parseInt(frequency) || 1;

    if (targetFrequency !== currentFieldsCount) {
      // Generate default times based on frequency
      const defaultTimes = generateDefaultTimes(targetFrequency);
      replace(defaultTimes.map(time => ({ value: time })));
    }
  }, [frequency, fields.length, replace]);

  // Generate sensible default times based on frequency
  const generateDefaultTimes = (freq) => {
    const times = [];
    switch (freq) {
      case 1:
        times.push('08:00');
        break;
      case 2:
        times.push('08:00', '20:00');
        break;
      case 3:
        times.push('08:00', '14:00', '20:00');
        break;
      case 4:
        times.push('08:00', '12:00', '16:00', '20:00');
        break;
      case 5:
        times.push('07:00', '11:00', '15:00', '19:00', '23:00');
        break;
      case 6:
        times.push('06:00', '10:00', '14:00', '18:00', '22:00', '02:00');
        break;
      default:
        // For any other frequency, distribute evenly across 24 hours
        const interval = Math.floor(24 / freq);
        for (let i = 0; i < freq; i++) {
          const hour = (8 + (i * interval)) % 24;
          times.push(`${hour.toString().padStart(2, '0')}:00`);
        }
    }
    return times;
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      // Extract timing values from the field array format
      const timingValues = data.timing.map(item => 
        typeof item === 'string' ? item : item.value
      ).filter(time => time && time !== '');

      await addMedicine({
        ...data,
        frequency: parseInt(data.frequency),
        timing: timingValues
      });
      toast.success('Medicine added successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to add medicine');
    } finally {
      setLoading(false);
    }
  };

  const medicineTypes = [
    { value: 'tablet', label: 'Tablet' },
    { value: 'capsule', label: 'Capsule' },
    { value: 'liquid', label: 'Liquid/Syrup' },
    { value: 'injection', label: 'Injection' },
    { value: 'drops', label: 'Drops' },
    { value: 'cream', label: 'Cream/Ointment' },
    { value: 'inhaler', label: 'Inhaler' },
    { value: 'patch', label: 'Patch' }
  ];

  const foodInstructions = [
    { value: 'before_food', label: 'Before food' },
    { value: 'after_food', label: 'After food' },
    { value: 'with_food', label: 'With food' },
    { value: 'empty_stomach', label: 'Empty stomach' },
    { value: 'anytime', label: 'Anytime' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/dashboard')}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiArrowLeft} className="text-xl" />
              </motion.button>
              <h1 className="text-2xl font-bold text-gray-900">Add New Medicine</h1>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8"
        >
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            {/* Basic Information */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Basic Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Medicine Name *
                  </label>
                  <input
                    type="text"
                    {...register('name', { required: 'Medicine name is required' })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter medicine name"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type *
                  </label>
                  <select
                    {...register('type', { required: 'Medicine type is required' })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  >
                    {medicineTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  {errors.type && (
                    <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Dosage *
                  </label>
                  <input
                    type="text"
                    {...register('dosage', { required: 'Dosage is required' })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="e.g., 500mg, 1 tablet, 5ml"
                  />
                  {errors.dosage && (
                    <p className="mt-1 text-sm text-red-600">{errors.dosage.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Food Instruction
                  </label>
                  <select
                    {...register('food_instruction')}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  >
                    {foodInstructions.map(instruction => (
                      <option key={instruction.value} value={instruction.value}>
                        {instruction.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Schedule */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Schedule</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Frequency (times per day) *
                  </label>
                  <select
                    {...register('frequency', { required: 'Frequency is required' })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  >
                    {[1, 2, 3, 4, 5, 6, 8, 12].map(num => (
                      <option key={num} value={num}>
                        {num} time{num > 1 ? 's' : ''} per day
                      </option>
                    ))}
                  </select>
                  {errors.frequency && (
                    <p className="mt-1 text-sm text-red-600">{errors.frequency.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    {...register('start_date', { required: 'Start date is required' })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  />
                  {errors.start_date && (
                    <p className="mt-1 text-sm text-red-600">{errors.start_date.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date (optional)
                  </label>
                  <input
                    type="date"
                    {...register('end_date')}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  />
                </div>
              </div>

              {/* Timing */}
              <div className="mt-6">
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Daily Timing * ({frequency} dose{frequency > 1 ? 's' : ''} per day)
                  </label>
                  <span className="text-sm text-gray-500">
                    {fields.length} of {frequency} time slots
                  </span>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {fields.map((field, index) => (
                    <motion.div
                      key={field.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      className="flex flex-col space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-gray-600">
                          Dose {index + 1}
                        </label>
                        {frequency > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              if (fields.length > 1) {
                                remove(index);
                                // Also decrease frequency to match
                                setValue('frequency', fields.length - 1);
                              }
                            }}
                            className="text-red-500 hover:text-red-700 p-1"
                          >
                            <SafeIcon icon={FiTrash2} className="text-sm" />
                          </button>
                        )}
                      </div>
                      <input
                        type="time"
                        {...register(`timing.${index}.value`, { 
                          required: 'Time is required' 
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      />
                      {errors.timing?.[index]?.value && (
                        <p className="text-xs text-red-600">
                          {errors.timing[index].value.message}
                        </p>
                      )}
                    </motion.div>
                  ))}
                </div>

                {/* Add more timing slots if needed */}
                {fields.length < 12 && (
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={() => {
                        append({ value: '12:00' });
                        setValue('frequency', fields.length + 1);
                      }}
                      className="flex items-center space-x-2 px-4 py-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
                    >
                      <SafeIcon icon={FiPlus} className="text-sm" />
                      <span>Add Another Time Slot</span>
                    </button>
                  </div>
                )}

                {/* Helpful timing suggestions */}
                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">Timing Suggestions:</h4>
                  <div className="text-sm text-blue-700 space-y-1">
                    <div><strong>1x daily:</strong> Morning (8:00 AM)</div>
                    <div><strong>2x daily:</strong> Morning & Evening (8:00 AM, 8:00 PM)</div>
                    <div><strong>3x daily:</strong> Morning, Afternoon & Evening (8:00 AM, 2:00 PM, 8:00 PM)</div>
                    <div><strong>4x daily:</strong> Every 6 hours (8:00 AM, 12:00 PM, 4:00 PM, 8:00 PM)</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Notes
              </label>
              <textarea
                {...register('notes')}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Any additional instructions or notes..."
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
              <motion.button
                type="button"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/dashboard')}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </motion.button>
              <motion.button
                type="submit"
                disabled={loading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-purple-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Adding Medicine...' : 'Add Medicine'}
              </motion.button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default AddMedicineForm;